import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AllergyLookup from "@/components/allergy/AllergyLookup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ALG } from "@/constants/testIds";
import { allergenTypeLabel } from "@/lib/allergyLookup";
import {
  ALLERGY_KINDS,
  allergyApiError,
  defaultCategoryForAllergenType,
  emptyForm,
  firstManifestation,
  formFromAllergy,
  formatReviewedAt,
  formatSeverity,
  formatStatus,
  formatVerification,
  formatWhen,
  highestSeverity,
  kindFromRecord,
  kindLabel,
  toAllergyPayload,
} from "@/lib/allergyCatalog";
import {
  createPatientAllergy,
  listPatientAllergies,
  recordNoKnownAllergies,
  removePatientAllergy,
  reviewPatientAllergies,
  updatePatientAllergy,
} from "@/lib/patientAllergyApi";
import "@/styles/emr-allergies.css";

function FieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block font-medium text-slate-600">
      {children}
    </label>
  );
}

function selectClass() {
  return "browser-default allergies-control w-full rounded-md border border-[#e4d8c8] bg-white text-slate-800";
}

export default function AllergiesWorkspace({
  patientId,
  patient,
  currentUser,
  onClose,
  onChange,
  accessReadOnly = false,
}) {
  const [items, setItems] = useState([]);
  const [review, setReview] = useState(null);
  const [noKnownAllergies, setNoKnownAllergies] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [mode, setMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  function applyList(data) {
    const nextItems = data.items || [];
    const nextReview = data.review || null;
    const nextNka = !!data.noKnownAllergies;
    setItems(nextItems);
    setReview(nextReview);
    setNoKnownAllergies(nextNka);
    onChange?.({ items: nextItems, review: nextReview, noKnownAllergies: nextNka });
  }

  async function reload() {
    const data = await listPatientAllergies(patientId, { includeInactive });
    applyList(data);
    return data;
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listPatientAllergies(patientId, { includeInactive })
      .then((data) => {
        if (cancelled) return;
        applyList(data);
      })
      .catch((error) => {
        if (cancelled) return;
        setItems([]);
        setReview(null);
        setNoKnownAllergies(false);
        setLoadError(allergyApiError(error, "Unable to load this patient's allergies."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // applyList is stable enough via onChange; include patientId/includeInactive only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, includeInactive]);

  function openAdd() {
    setMode("add");
    setEditingId(null);
    setSaveError(null);
    setForm(emptyForm());
  }

  function openEdit(item) {
    setMode("edit");
    setEditingId(item.id);
    setSaveError(null);
    setForm(formFromAllergy(item));
  }

  function cancelForm() {
    setMode(null);
    setEditingId(null);
    setSaveError(null);
    setForm(emptyForm());
  }

  function onSelectAllergen(selected, item) {
    if (!selected) {
      setForm((current) => ({ ...current, allergenId: "", selected: null }));
      return;
    }
    const type = selected.allergenType || item?.allergenType;
    setForm((current) => ({
      ...current,
      allergenId: selected.id,
      selected: {
        id: selected.id,
        displayName: selected.displayName,
        allergenType: type,
        allergenTypeLabel: item?.allergenTypeLabel || selected.allergenTypeLabel,
      },
      category: current.category || defaultCategoryForAllergenType(type) || "",
    }));
  }

  async function save() {
    if (!form.allergenId || saving) return;
    setSaving(true);
    setSaveError(null);
    const payload = toAllergyPayload({
      ...form,
      clearReaction: mode === "edit" && !form.manifestation,
    });
    try {
      if (mode === "edit" && editingId) {
        const update = { ...payload };
        if (update.allergenId === items.find((row) => row.id === editingId)?.allergenId) {
          delete update.allergenId;
        }
        await updatePatientAllergy(editingId, update);
        toast.success("Allergy updated");
      } else {
        await createPatientAllergy(patientId, payload);
        toast.success("Allergy recorded");
      }
      await reload();
      cancelForm();
    } catch (error) {
      const message = allergyApiError(error, "Unable to save this allergy.");
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function markReviewed() {
    try {
      await reviewPatientAllergies(patientId);
      await reload();
      toast.success("Allergy list marked as reviewed");
    } catch (error) {
      toast.error(allergyApiError(error, "Unable to mark the allergy list as reviewed."));
    }
  }

  async function attestNka() {
    try {
      await recordNoKnownAllergies(patientId);
      await reload();
      toast.success("No known allergies recorded");
    } catch (error) {
      toast.error(allergyApiError(error, "Unable to record no known allergies."));
    }
  }

  async function confirmDeactivate() {
    if (!deleteTarget) return;
    try {
      await removePatientAllergy(deleteTarget.id);
      toast.success("Allergy deactivated");
      setDeleteTarget(null);
      if (editingId === deleteTarget.id) cancelForm();
      await reload();
    } catch (error) {
      toast.error(allergyApiError(error, "Unable to deactivate this allergy."));
    }
  }

  const reviewerName = currentUser?.displayName || currentUser?.email || "clinician";
  const readOnlyLookup = accessReadOnly;

  return (
    <div
      data-testid={ALG.panel}
      className="allergies-section overflow-hidden rounded-lg border border-[#e4d8c8] bg-white shadow-[0_1px_2px_rgba(50,32,12,0.06)]"
    >
      <div className="flex items-center justify-between border-b border-[#e4d8c8] bg-[#F6F2FB] px-5 py-3">
        <h2 data-testid={ALG.title} className="font-semibold text-[#6B4C9A]">
          Allergies / Contraindications
        </h2>
        {onClose ? (
          <button
            type="button"
            data-testid={ALG.close}
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <section data-testid={ALG.review} className="border-b border-[#e4d8c8] px-5 py-4">
        <h3 className="font-semibold text-[#6B4C9A]">Allergy list review</h3>
        <p data-testid={ALG.reconcileNote} className="mt-1 text-slate-600">
          Outside allergy import is not connected. Review the in-chart list, then mark as reviewed.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {accessReadOnly ? null : (
          <Button
            type="button"
            variant="outline"
            data-testid={ALG.markReviewed}
            onClick={markReviewed}
            className="allergies-toolbar-btn border-[#6B4C9A] text-[#6B4C9A] hover:bg-[#F3EEF8]"
          >
            <Check className="h-4 w-4" />
            Mark as Reviewed
          </Button>
          )}
          <p data-testid={ALG.lastReviewed} className="text-slate-600">
            {review
              ? `Last reviewed by ${review.reviewedByDisplay || reviewerName} on ${formatReviewedAt(review.reviewedAt)}`
              : "Never reviewed"}
          </p>
        </div>
      </section>

      <section className="px-5 py-4">
        {loadError ? (
          <p
            data-testid={ALG.loadError}
            role="alert"
            className="mb-3 text-sm text-amber-700"
          >
            {loadError}
          </p>
        ) : null}

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-[#6B4C9A]">Patient allergy list</h3>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-slate-600">
              <input
                type="checkbox"
                className="browser-default"
                data-testid={ALG.includeInactive}
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
              />
              Include inactive
            </label>
            {accessReadOnly ? null : (
            <Button
              type="button"
              data-testid={ALG.add}
              onClick={openAdd}
              className="allergies-toolbar-btn bg-[#6B4C9A] text-white hover:bg-[#5A3D86]"
            >
              <Plus className="h-4 w-4" />
              Add Allergy
            </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading allergies…
          </div>
        ) : noKnownAllergies && items.length === 0 ? (
          <div
            data-testid={ALG.nka}
            className="rounded-md border border-[#e4d8c8] bg-[#FBF8FF] px-4 py-6 text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-wide text-[#6B4C9A]">
              No known allergies
            </p>
            <p className="mt-1 text-sm text-slate-600">
              A clinician attested that this patient has no known allergies. This is not an
              allergen from the catalog. If an allergy is later identified, use Add Allergy.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid={ALG.emptyState}
            className="rounded-md border border-[#e4d8c8] bg-[#FBF8FF] px-4 py-6 text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-wide text-[#6B4C9A]">
              No allergies on file
            </p>
            <p className="mt-1 text-sm text-slate-600">
              No allergies or contraindications have been documented. This is not the same as no
              known allergies.
            </p>
            {accessReadOnly ? null : (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button
                type="button"
                onClick={openAdd}
                className="allergies-toolbar-btn bg-[#6B4C9A] text-white hover:bg-[#5A3D86]"
              >
                Add Allergy
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid={ALG.nkaButton}
                onClick={attestNka}
                className="allergies-toolbar-btn border-[#6B4C9A] text-[#6B4C9A] hover:bg-[#F3EEF8]"
              >
                No Known Allergies
              </Button>
            </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-[#e4d8c8]">
            <table
              data-testid={ALG.table}
              aria-label="Patient allergies"
              className="allergies-table w-full min-w-[880px]"
            >
              <thead className="bg-[#F6F2FB] text-left font-semibold text-slate-500">
                <tr>
                  <th scope="col">
                    Allergen
                  </th>
                  <th scope="col">
                    Type
                  </th>
                  <th scope="col">
                    Reaction
                  </th>
                  <th scope="col">
                    Severity
                  </th>
                  <th scope="col">
                    Status
                  </th>
                  <th scope="col">
                    Verification
                  </th>
                  <th scope="col">
                    Last Updated
                  </th>
                  <th className="text-right" scope="col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    data-testid={`${ALG.row}-${item.id}`}
                    className="border-t border-[#f0e6d8]"
                  >
                    <td className="allergies-allergen-cell font-medium text-[#6B4C9A]">
                      <span>{item.allergenDisplayName}</span>
                      {item.allergenType ? (
                        <span className="font-normal text-slate-500">
                          {" "}
                          {allergenTypeLabel(item.allergenType)}
                        </span>
                      ) : null}
                    </td>
                    <td className="text-slate-700">{kindLabel(kindFromRecord(item))}</td>
                    <td className="text-slate-700">{firstManifestation(item) || "—"}</td>
                    <td className="text-slate-700">
                      {formatSeverity(highestSeverity(item))}
                    </td>
                    <td className="text-slate-700">{formatStatus(item.clinicalStatus)}</td>
                    <td className="text-slate-700">
                      {formatVerification(item.verificationStatus)}
                    </td>
                    <td className="text-slate-600">
                      {formatWhen(item.updatedAt || item.recordedDate)}
                    </td>
                    <td>
                      {accessReadOnly ? null : (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          data-testid={`${ALG.edit}-${item.id}`}
                          onClick={() => openEdit(item)}
                          className="allergies-row-btn rounded text-slate-500 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
                          aria-label={`Edit ${item.allergenDisplayName}`}
                        >
                          <Pencil />
                        </button>
                        <button
                          type="button"
                          data-testid={`${ALG.deactivate}-${item.id}`}
                          onClick={() => setDeleteTarget(item)}
                          className="allergies-row-btn rounded text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                          aria-label={`Deactivate ${item.allergenDisplayName}`}
                        >
                          <Trash2 />
                        </button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {mode && !accessReadOnly ? (
        <section
          data-testid={ALG.form}
          className="border-t border-[#e4d8c8] px-5 py-4"
        >
          <h3 className="mb-3 font-semibold text-[#6B4C9A]">
            {mode === "edit" ? "Edit allergy" : "Add allergy"}
          </h3>
          <AllergyLookup
            onSelect={onSelectAllergen}
            selected={form.selected}
            readOnly={readOnlyLookup}
            autoFocus={mode === "add"}
          />

          {form.selected ? (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div>
                <FieldLabel htmlFor="allergy-kind">Record as</FieldLabel>
                <select
                  id="allergy-kind"
                  data-testid={ALG.kind}
                  className={selectClass()}
                  value={form.kind}
                  onChange={(event) => setForm((current) => ({ ...current, kind: event.target.value }))}
                >
                  {ALLERGY_KINDS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-status">Clinical status</FieldLabel>
                <select
                  id="allergy-status"
                  data-testid={ALG.clinicalStatus}
                  className={selectClass()}
                  value={form.clinicalStatus}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, clinicalStatus: event.target.value }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-verification">Verification</FieldLabel>
                <select
                  id="allergy-verification"
                  data-testid={ALG.verification}
                  className={selectClass()}
                  value={form.verificationStatus}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, verificationStatus: event.target.value }))
                  }
                >
                  <option value="unconfirmed">Unconfirmed</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="refuted">Refuted</option>
                  <option value="entered-in-error">Entered in error</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-criticality">Criticality</FieldLabel>
                <select
                  id="allergy-criticality"
                  data-testid={ALG.criticality}
                  className={selectClass()}
                  value={form.criticality}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, criticality: event.target.value }))
                  }
                >
                  <option value="">Not specified</option>
                  <option value="low">Low</option>
                  <option value="high">High</option>
                  <option value="unable-to-assess">Unable to assess</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-category">Category</FieldLabel>
                <select
                  id="allergy-category"
                  data-testid={ALG.category}
                  className={selectClass()}
                  value={form.category}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, category: event.target.value }))
                  }
                >
                  <option value="">Select category</option>
                  <option value="medication">Medication</option>
                  <option value="food">Food</option>
                  <option value="environment">Environment</option>
                  <option value="biologic">Biologic</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-onset">Onset date</FieldLabel>
                <Input
                  id="allergy-onset"
                  type="date"
                  data-testid={ALG.onset}
                  value={form.onsetDate}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, onsetDate: event.target.value }))
                  }
                  className="browser-default allergies-control bg-white"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel htmlFor="allergy-manifestation">Reaction</FieldLabel>
                <Input
                  id="allergy-manifestation"
                  data-testid={ALG.manifestation}
                  value={form.manifestation}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, manifestation: event.target.value }))
                  }
                  placeholder="e.g. Hives, rash, anaphylaxis"
                  className="browser-default allergies-control bg-white"
                />
              </div>
              <div>
                <FieldLabel htmlFor="allergy-severity">Reaction severity</FieldLabel>
                <select
                  id="allergy-severity"
                  data-testid={ALG.severity}
                  className={selectClass()}
                  value={form.severity}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, severity: event.target.value }))
                  }
                >
                  <option value="">Not specified</option>
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="allergy-route">Exposure route</FieldLabel>
                <Input
                  id="allergy-route"
                  data-testid={ALG.route}
                  value={form.exposureRoute}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, exposureRoute: event.target.value }))
                  }
                  placeholder="e.g. oral"
                  className="browser-default allergies-control bg-white"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel htmlFor="allergy-description">Reaction description</FieldLabel>
                <Input
                  id="allergy-description"
                  data-testid={ALG.description}
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, description: event.target.value }))
                  }
                  placeholder="Optional clinical detail"
                  className="browser-default allergies-control bg-white"
                />
              </div>
              <div className="md:col-span-3">
                <FieldLabel htmlFor="allergy-note">Notes</FieldLabel>
                <Input
                  id="allergy-note"
                  data-testid={ALG.note}
                  value={form.note}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, note: event.target.value }))
                  }
                  className="browser-default allergies-control bg-white"
                />
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              Search and select an allergen. Terminology codes are filled from the catalog, not typed
              here.
            </p>
          )}

          {saveError ? (
            <p
              data-testid={ALG.saveError}
              role="alert"
              className="mt-3 text-sm text-rose-700"
            >
              {saveError}
            </p>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              data-testid={ALG.save}
              disabled={!form.allergenId || saving}
              onClick={save}
              className="allergies-toolbar-btn bg-[#6B4C9A] text-white hover:bg-[#5A3D86]"
            >
              {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save allergy"}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid={ALG.cancel}
              onClick={cancelForm}
              className="allergies-toolbar-btn border-[#e4d8c8]"
            >
              Cancel
            </Button>
          </div>
        </section>
      ) : null}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={ALG.deactivateDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this allergy?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {deleteTarget?.allergenDisplayName} from the active allergy list. The
              clinical history is kept. The allergen catalog is not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ALG.deactivateConfirm}
              onClick={confirmDeactivate}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
